import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface Task {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  due_date?: string;
  assigned_date?: string;
  completed_at?: string;
  attachments?: any[];
  parent_task_id?: number;
  project_id?: number;
  blocks?: string[];
  tags?: string[]; // Tags associated with the task
  goals?: Array<{ id: string; title: string }>; // Goals associated with the task
  total_minutes?: number; // Total time logged for this task
  created_at: string;
  updated_at: string;
}

export interface TaskFormData {
  title: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  due_date?: string;
  assigned_date?: string;
  attachments?: any[];
  parent_task_id?: number;
  project_id?: number;
  blocks?: string[];
  tag_names?: string[];
}

export interface CreateTaskData {
  user_id: string;
  title: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  due_date?: string;
  assigned_date?: string;
  attachments?: any[];
  parent_task_id?: number;
  project_id?: number;
  blocks?: string[];
  tag_names?: string[];
}

export interface UpdateTaskData {
  id: string;
  title?: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  due_date?: string;
  assigned_date?: string;
  completed_at?: string;
  attachments?: any[];
  parent_task_id?: number;
  project_id?: number | null;
  blocks?: string[];
  tag_names?: string[];
}

export interface TaskFilters {
  user_id: string;
  status?: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  due_date?: string;
  assigned_date?: string;
  parent_task_id?: number;
  search_term?: string;
  limit?: number;
  offset?: number;
}

class TaskService {
  // Create a new task
  async createTask(userId: string, taskData: TaskFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const data: CreateTaskData = {
        user_id: userId,
        title: taskData.title,
        description: taskData.description || undefined,
        status: taskData.status || 'todo',
        due_date: taskData.due_date || undefined,
        assigned_date: taskData.assigned_date || undefined,
        attachments: taskData.attachments || [],
        parent_task_id: taskData.parent_task_id || undefined,
        project_id: taskData.project_id || undefined,
        blocks: taskData.blocks || undefined,
        tag_names: taskData.tag_names || undefined
      };

      const response = await makeAuthenticatedRequest('/tasks', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  // Get task by ID
  async getTaskById(taskId: string): Promise<{ success: boolean; task?: Task }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching task:', error);
      throw error;
    }
  }

  // Get all tasks for a user
  async getUserTasks(filters: TaskFilters): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.due_date) queryParams.append('due_date', filters.due_date);
      if (filters.assigned_date) queryParams.append('assigned_date', filters.assigned_date);
      if (filters.parent_task_id) queryParams.append('parent_task_id', filters.parent_task_id.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/tasks/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user tasks:', error);
      throw error;
    }
  }

  // Update a task
  async updateTask(updateData: UpdateTaskData): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('TaskService updating task:', updateData);
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  // Delete a task
  async deleteTask(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // Search tasks
  async searchTasks(filters: TaskFilters): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.due_date) queryParams.append('due_date', filters.due_date);
      if (filters.assigned_date) queryParams.append('assigned_date', filters.assigned_date);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/tasks/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching tasks:', error);
      throw error;
    }
  }

  // Get subtasks for a specific task
  async getSubtasks(taskId: string, filters?: { status?: string; limit?: number; offset?: number }): Promise<{ success: boolean; subtasks?: Task[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/tasks/${taskId}/subtasks${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      throw error;
    }
  }

  // Get tasks due today
  async getTasksDueToday(userId: string, status?: string): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      
      const queryString = queryParams.toString();
      const url = `/tasks/due-today/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching tasks due today:', error);
      throw error;
    }
  }

  // Get tasks assigned for a specific date
  async getTasksAssignedForDate(userId: string, assignedDate: string, status?: string): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      
      const queryString = queryParams.toString();
      const url = `/tasks/assigned/${userId}/${assignedDate}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching tasks assigned for date:', error);
      throw error;
    }
  }

  // Mark task as completed
  async completeTask(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'completed'
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  }

  // Mark task as in progress
  async startTask(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'in_progress'
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error starting task:', error);
      throw error;
    }
  }

  // Put task on hold
  async putTaskOnHold(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'on_hold'
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error putting task on hold:', error);
      throw error;
    }
  }

  // Cancel task
  async cancelTask(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'cancelled'
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error cancelling task:', error);
      throw error;
    }
  }

  // Get tasks by status
  async getTasksByStatus(userId: string, status: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled', limit?: number): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      const filters: TaskFilters = {
        user_id: userId,
        status: status,
        limit: limit || 50
      };
      
      return await this.getUserTasks(filters);
    } catch (error) {
      console.error('Error fetching tasks by status:', error);
      throw error;
    }
  }

  // Get overdue tasks
  async getOverdueTasks(userId: string): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const response = await makeAuthenticatedRequest(`/tasks/user/${userId}?due_date=${today}&status=todo`, {
        method: 'GET'
      });
      
      // Filter for tasks that are overdue (due_date < today and status is not completed)
      if (response.success && response.tasks) {
        const overdueTasks = response.tasks.filter((task: Task) => {
          if (!task.due_date || task.status === 'completed') return false;
          return task.due_date < today;
        });
        
        return {
          success: true,
          tasks: overdueTasks,
          count: overdueTasks.length
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching overdue tasks:', error);
      throw error;
    }
  }

  // Get tasks for a specific project
  async getProjectTasks(projectId: string, filters?: { status?: string }): Promise<{ success: boolean; tasks?: Task[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      
      const queryString = queryParams.toString();
      const url = `/projects/${projectId}/tasks${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      throw error;
    }
  }

  // Convert API task to our Task interface
  convertApiTask(apiTask: any): Task {
    return {
      id: apiTask.id,
      user_id: apiTask.user_id,
      title: apiTask.title,
      description: apiTask.description,
      status: apiTask.status,
      due_date: apiTask.due_date,
      assigned_date: apiTask.assigned_date,
      completed_at: apiTask.completed_at,
      attachments: apiTask.attachments || [],
      parent_task_id: apiTask.parent_task_id,
      project_id: apiTask.project_id,
      blocks: apiTask.blocks || [],
      tags: apiTask.tags || [],
      goals: apiTask.goals || [],
      total_minutes: apiTask.total_minutes || 0,
      created_at: apiTask.created_at,
      updated_at: apiTask.updated_at
    };
  }

  // Add blocks to a task
  async addBlocksToTask(taskId: string, blockIds: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // Get current task to retrieve existing blocks
      const taskResponse = await this.getTaskById(taskId);
      if (!taskResponse.success || !taskResponse.task) {
        return { success: false, message: 'Task not found' };
      }

      const currentBlocks = taskResponse.task.blocks || [];
      const newBlocks = [...new Set([...currentBlocks, ...blockIds])]; // Remove duplicates

      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          blocks: newBlocks
        })
      });

      return response;
    } catch (error) {
      console.error('Error adding blocks to task:', error);
      throw error;
    }
  }

  // Remove blocks from a task
  async removeBlocksFromTask(taskId: string, blockIds: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // Get current task to retrieve existing blocks
      const taskResponse = await this.getTaskById(taskId);
      if (!taskResponse.success || !taskResponse.task) {
        return { success: false, message: 'Task not found' };
      }

      const currentBlocks = taskResponse.task.blocks || [];
      const newBlocks = currentBlocks.filter(blockId => !blockIds.includes(blockId));

      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          blocks: newBlocks
        })
      });

      return response;
    } catch (error) {
      console.error('Error removing blocks from task:', error);
      throw error;
    }
  }

  // Set blocks for a task (replace all existing blocks)
  async setTaskBlocks(taskId: string, blockIds: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          blocks: blockIds
        })
      });

      return response;
    } catch (error) {
      console.error('Error setting task blocks:', error);
      throw error;
    }
  }

  // Get task statistics
  async getTaskStats(userId: string): Promise<{ 
    success: boolean; 
    stats?: {
      total: number;
      todo: number;
      in_progress: number;
      completed: number;
      on_hold: number;
      cancelled: number;
      overdue: number;
      due_today: number;
      assigned_today: number;
    }
  }> {
    try {
      // Get all tasks for the user
      const allTasksResponse = await this.getUserTasks({ user_id: userId, limit: 1000 });
      
      if (!allTasksResponse.success || !allTasksResponse.tasks) {
        return { success: false };
      }

      const tasks = allTasksResponse.tasks;
      const today = new Date().toISOString().split('T')[0];

      const stats = {
        total: tasks.length,
        todo: tasks.filter(task => task.status === 'todo').length,
        in_progress: tasks.filter(task => task.status === 'in_progress').length,
        completed: tasks.filter(task => task.status === 'completed').length,
        on_hold: tasks.filter(task => task.status === 'on_hold').length,
        cancelled: tasks.filter(task => task.status === 'cancelled').length,
        overdue: tasks.filter(task => 
          task.due_date && 
          task.due_date < today && 
          task.status !== 'completed'
        ).length,
        due_today: tasks.filter(task => 
          task.due_date === today && 
          task.status !== 'completed'
        ).length,
        assigned_today: tasks.filter(task => 
          task.assigned_date === today && 
          task.status !== 'completed'
        ).length
      };

      return {
        success: true,
        stats
      };
    } catch (error) {
      console.error('Error fetching task statistics:', error);
      throw error;
    }
  }
}

export const taskService = new TaskService();
